// shared/components/admin/EditAuthorizationTemplate.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';

import SecondaryButton from '../SecondaryButton';

const EditAuthorizationTemplate = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const getAuthorizationTemplate = useStoreActions(
        (a) => a.middleware.AuthorizationMiddleware.getAuthorizationTemplate
    );
    const updateAuthorizationTemplate = useStoreActions(
        (a) => a.middleware.AuthorizationMiddleware.updateAuthorizationTemplate
    );
    const toggleAuthorizationTemplateActive = useStoreActions(
        (a) => a.middleware.AuthorizationMiddleware.toggleAuthorizationTemplateActive
    );
    const deleteAuthorizationTemplate = useStoreActions(
        (a) => a.middleware.AuthorizationMiddleware.deleteAuthorizationTemplate
    );

    const [loading, setLoading] = useState(true);

    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('');
    const [version, setVersion] = useState(1);
    const [isActive, setIsActive] = useState(true);
    const [bodyText, setBodyText] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const t = await getAuthorizationTemplate(id);
            if (!t?.id) throw new Error('template not found');
            setTitle(t.title || '');
            setCategory(t.category || '');
            setVersion(t.version ?? 1);
            setIsActive(!!t.isActive);
            setBodyText(t.bodyText || '');
        } catch (e) {
            console.log('❌ load template error', e);
            toast.error("❗️ Impossible de charger l’autorisation");
            navigate('/authorizations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const onSave = async (e) => {
        e.preventDefault();

        if (!title.trim()) {
            toast.error('❗️ Le titre est obligatoire');
            return;
        }
        if (!bodyText.trim()) {
            toast.error('❗️ Le texte est obligatoire');
            return;
        }

        try {
            await updateAuthorizationTemplate({
                id,
                title: title.trim(),
                bodyText,
                category: category.trim() || null,
                version: Number(version) || 1,
                isActive,
            });
            toast.success('✅ Autorisation mise à jour');
        } catch (err) {
            console.log('❌ updateAuthorizationTemplate error', err);
            toast.error("❗️ Erreur lors de la mise à jour");
        }
    };

    const onToggle = async () => {
        try {
            const next = !isActive;
            setIsActive(next);
            await toggleAuthorizationTemplateActive({ id, isActive: next });
            toast.success(next ? '✅ Autorisation activée' : '✅ Autorisation désactivée');
        } catch (err) {
            console.log('❌ toggle error', err);
            toast.error("❗️ Erreur lors du changement d’état");
        }
    };

    const onDelete = async () => {
        const ok = window.confirm(
            "Supprimer ce template ? Cette action est définitive."
        );
        if (!ok) return;

        try {
            await deleteAuthorizationTemplate(id);
            toast.success('✅ Autorisation supprimée');
            navigate('/authorizations');
        } catch (err) {
            console.log('❌ delete error', err);
            toast.error("❗️ Erreur lors de la suppression");
        }
    };

    if (loading) {
        return (
            <section className="md:pt-14 px-4 md:px-9">
                <div className="border-b border-gray-admin pb-4 pt-11 md:pt-0 mb-4">
                    <h3 className="font-bold text-xl">Chargement...</h3>
                </div>
            </section>
        );
    }

    return (
        <section className="md:pt-14 px-4 md:px-9">
            <div className="flex flex-col space-y-4 items-center justify-between border-b border-gray-admin pb-4 pt-11 md:pt-0 mb-4 md:flex-row md:space-y-0">
                <h3 className="font-bold text-xl">Modifier l’autorisation</h3>
                <div className="flex items-center gap-3">
                    <SecondaryButton title="← Retour" link={'/authorizations'} />
                </div>
            </div>

            <form onSubmit={onSave} className="mt-6 max-w-3xl space-y-4">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={onToggle}
                        className={`px-3 py-2 rounded-lg font-semibold text-sm ${isActive ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'
                            }`}
                    >
                        {isActive ? 'Désactiver' : 'Activer'}
                    </button>

                    <button
                        type="button"
                        onClick={onDelete}
                        className="px-3 py-2 rounded-lg font-semibold text-sm bg-red-100 text-red-700"
                    >
                        Supprimer
                    </button>
                </div>

                <div>
                    <label className="block text-sm font-semibold mb-1">Titre *</label>
                    <input
                        className="w-full border rounded-lg px-3 py-2"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <label className="block text-sm font-semibold mb-1">Catégorie</label>
                        <input
                            className="w-full border rounded-lg px-3 py-2"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-1">Version</label>
                        <input
                            type="number"
                            className="w-full border rounded-lg px-3 py-2"
                            value={version}
                            onChange={(e) => setVersion(e.target.value)}
                            min={1}
                        />
                    </div>

                    <div className="flex items-center gap-2 mt-6">
                        <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                        />
                        <span className="text-sm">Actif</span>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold mb-1">Texte *</label>
                    <textarea
                        className="w-full border rounded-lg px-3 py-2 min-h-[220px]"
                        value={bodyText}
                        onChange={(e) => setBodyText(e.target.value)}
                    />
                </div>

                <div className="pt-2 flex gap-3">
                    <button
                        type="submit"
                        className="bg-blue-admin text-white font-semibold px-4 py-2 rounded-lg"
                    >
                        Enregistrer
                    </button>
                </div>
            </form>
        </section>
    );
};

export default EditAuthorizationTemplate;
